import React from 'react';
import Color from 'color';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import {
  BackgroundConfig,
  BackgroundType,
  BackgroundImageDisplayMode,
} from '../types';
import { DEFAULT_COLOR } from '../constants';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks/useFlag';

import s from './Background.scss';

export const gradientToClassName = (gradient?: number): string => {
  return (gradient && s[`gradient-type-${gradient}`]) || '';
};

export interface Props extends BackgroundConfig {
  className?: string;
  children?: React.ReactNode;
}

const Background: React.SFC<Props> = ({
  backgroundType,
  className,
  color,
  gradient,
  pattern,
  imageDisplayMode,
  imageTint,
  image,
  children,
}) => {
  const customboxThemeDefaults = useFlag('custombox_theme_defaults');
  const classNames = [s.root, className];
  const styles: React.CSSProperties = {};
  switch (backgroundType) {
    case BackgroundType.SOLID:
      if (color) {
        styles.backgroundColor = Color(color || DEFAULT_COLOR).string();
        // our original default was SOLID with undefined color (which would
        // default to white) we want this to be theme-based instead
      } else if (customboxThemeDefaults) {
        classNames.push(
          s['theme-based'],
          // we also need a global css class that can be picked up by
          // shared's DaEditorWrapper custombox.scss
          'custombox_using_theme_defaults'
        );
      }
      break;
    case BackgroundType.GRADIENT:
      classNames.push(gradientToClassName(gradient));
      break;
    case BackgroundType.PATTERN:
      classNames.push(s['display-mode-tile']);
      styles.background = `url(${pattern})`;
      break;
    case BackgroundType.IMAGE:
      const file = MediaService.getLargestFullview(image!);
      styles.backgroundImage = `url(${file!.src})`;
      if (imageTint) {
        styles.backgroundImage = `linear-gradient(${imageTint}, ${imageTint}), ${styles.backgroundImage}`;
      }
      switch (imageDisplayMode) {
        case BackgroundImageDisplayMode.COVER:
          classNames.push(s['display-mode-cover']);
          break;
        case BackgroundImageDisplayMode.CONTAIN:
          classNames.push(s['display-mode-contain']);
          break;
        case BackgroundImageDisplayMode.TILE:
          classNames.push(s['display-mode-repeat']);
          break;
        default:
          ddt.error('CustomBox', 'Unknown image display mode');
      }
      break;
    default:
      ddt.error('CustomBox', 'Unknown background type');
  }
  return (
    <div className={classnames(classNames)} style={styles}>
      {children}
    </div>
  );
};
Background.displayName = 'CustomBoxBackground';

export default Background;
